// ── WorkflowCanvasBuilder ──────────────────────────────────────────
//
// Full-screen visual builder for the v2 `definition` tree:
//   • Channel nodes (Send WhatsApp/Email/SMS/Notify) with recipient
//     sub-boxes (Client / Counselor / Admin), each its own template/msg.
//   • Wait nodes.
//   • If/Else nodes that branch into Yes / No lanes of further steps.
//
// Produces a WorkflowDefinition that the backend engine tree-walks.

import { useEffect, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { toast } from '@/components/ui/toast';
import api from '@/lib/api';
import {
  createWorkflow,
  updateWorkflow,
  type Workflow,
  type WorkflowDefinition,
  type DefNode,
  type ChannelKey,
  type RecipientKey,
  type TriggerType,
  type TemplateOption,
} from '../api/workflow.api';
import { TimeDropdown } from '@/components/TimeDropdown';
import { SelectField } from '@/components/ui/SelectField';

// ── meta ───────────────────────────────────────────────────────────
const CH_META: Record<ChannelKey, { icon: string; bg: string; fg: string; title: string }> = {
  WHATSAPP: { icon: '📱', bg: '#dcfce7', fg: '#16a34a', title: 'Send WhatsApp' },
  EMAIL: { icon: '✉️', bg: '#dbeafe', fg: '#2563eb', title: 'Send Email' },
  SMS: { icon: '💬', bg: '#fef3c7', fg: '#d97706', title: 'Send SMS' },
  NOTIFY: { icon: '🔔', bg: '#ede9fe', fg: '#7c3aed', title: 'In-app notify' },
  CALL: { icon: '📞', bg: '#e0e7ff', fg: '#4f46e5', title: 'AI Voice Call' },
};
const TRIGGER_LABELS: Record<TriggerType, string> = {
  LEAD_CREATED: 'New lead created',
  LEAD_ASSIGNED: 'Lead assigned to counselor',
  LEAD_STATUS_CHANGED: 'Lead status changed',
  MEETING_BOOKED: 'Meeting booked',
  FOLLOWUP_DUE: 'Follow-up due',
  MEETING_UPCOMING: 'Meeting starting soon',
};
// FOLLOWUP_DUE: how long before the follow-up's due time the workflow starts.
const FOLLOWUP_OFFSETS: { value: number; label: string }[] = [
  { value: 0, label: 'At the due time' },
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 1440, label: '1 day before' },
];
const RICON: Record<RecipientKey, string> = { Client: '👤', Counselor: '🧑‍💼', Admins: '🛡️' };
const COND_FIELDS: Record<string, string> = {
  replied: 'Client replied?', status: 'Lead status', source: 'Lead source', hot: 'Lead is hot',
};
const VAR_FIELDS = [
  { value: 'lead.name', label: 'Lead name' }, { value: 'lead.firstName', label: 'Lead first name' },
  { value: 'lead.phone', label: 'Lead phone' }, { value: 'lead.email', label: 'Lead email' },
  { value: 'lead.leadNo', label: 'Lead number' }, { value: 'lead.source', label: 'Lead source' },
  { value: 'counselor.name', label: 'Counselor name' }, { value: 'counselor.phone', label: 'Counselor phone' },
  { value: 'company.name', label: 'Company name' },
  { value: 'voice.fromNumber', label: 'AI call number' },
];
// Extra variables available only for the event triggers.
const MEETING_VARS = [
  { value: 'meeting.event', label: 'Meeting type' },
  { value: 'meeting.guestName', label: 'Booked by (name)' },
  { value: 'meeting.date', label: 'Meeting date' },
  { value: 'meeting.time', label: 'Meeting time' },
  { value: 'meeting.timeWithZone', label: 'Meeting time + timezone' },
  { value: 'meeting.when', label: 'Meeting date & time (lead)' },
  { value: 'meeting.counselorWhen', label: 'Meeting date & time (counselor)' },
  { value: 'meeting.duration', label: 'Meeting duration' },
  { value: 'meeting.link', label: 'Meeting link' },
  { value: 'meeting.location', label: 'Meeting location' },
];
const EVENT_VAR_FIELDS: Partial<Record<TriggerType, { value: string; label: string }[]>> = {
  MEETING_UPCOMING: MEETING_VARS,
  MEETING_BOOKED: [
    { value: 'meeting.event', label: 'Meeting type' },
    { value: 'meeting.guestName', label: 'Booked by (name)' },
    { value: 'meeting.date', label: 'Meeting date' },
    { value: 'meeting.time', label: 'Meeting time' },
    { value: 'meeting.timeWithZone', label: 'Meeting time + timezone' },
    { value: 'meeting.when', label: 'Meeting date & time (lead)' },
    { value: 'meeting.counselorWhen', label: 'Meeting date & time (counselor)' },
    { value: 'meeting.duration', label: 'Meeting duration' },
    { value: 'meeting.link', label: 'Meeting link' },
    { value: 'meeting.location', label: 'Meeting location' },
  ],
  FOLLOWUP_DUE: [
    { value: 'followup.due', label: 'Follow-up due (date & time)' },
    { value: 'followup.title', label: 'Follow-up title' },
  ],
};

// WAIT delay is stored as minutes on the node; the UI lets the user pick a
// value + unit (Minutes / Hours / Days) and converts to/from minutes.
const DELAY_UNITS = [
  { unit: 'minutes', mins: 1, label: 'Minutes' },
  { unit: 'hours', mins: 60, label: 'Hours' },
  { unit: 'days', mins: 1440, label: 'Days' },
] as const;
type DelayUnit = (typeof DELAY_UNITS)[number]['unit'];

function splitDelay(totalMin: number): { value: number; unit: DelayUnit } {
  const m = Math.max(0, Number(totalMin) || 0);
  if (m > 0 && m % 1440 === 0) return { value: m / 1440, unit: 'days' };
  if (m > 0 && m % 60 === 0) return { value: m / 60, unit: 'hours' };
  return { value: m, unit: 'minutes' };
}
function unitMins(unit: DelayUnit): number {
  return DELAY_UNITS.find((u) => u.unit === unit)!.mins;
}
function formatDelay(totalMin: number): string {
  const { value, unit } = splitDelay(totalMin);
  const u = unit === 'minutes' ? 'min' : unit === 'hours' ? (value === 1 ? 'hour' : 'hours') : (value === 1 ? 'day' : 'days');
  return `${value} ${u}`;
}

function recipientsFor(ch: ChannelKey): RecipientKey[] {
  if (ch === 'NOTIFY') return ['Counselor', 'Admins'];
  if (ch === 'CALL') return ['Client']; // an AI call only targets the lead
  return ['Client', 'Counselor', 'Admins'];
}
function rLabel(r: RecipientKey) { return r === 'Admins' ? 'Admin' : r; }

// ── tree path helpers (Seg = {i, branch?}) ─────────────────────────
type Seg = { i: number; branch?: 'yes' | 'no' };
function arrAt(def: WorkflowDefinition, path: Seg[]): DefNode[] {
  let arr: DefNode[] = def.steps;
  for (let k = 0; k < path.length - 1; k++) {
    const n = arr[path[k].i] as any;
    arr = path[k].branch === 'no' ? n.no : n.yes;
  }
  return arr;
}
function nodeAt(def: WorkflowDefinition, path: Seg[]): DefNode | undefined {
  return arrAt(def, path)[path[path.length - 1].i];
}
function clone(d: WorkflowDefinition): WorkflowDefinition { return JSON.parse(JSON.stringify(d)); }

function newSend(ch: ChannelKey, recipient?: RecipientKey): DefNode {
  const r = recipient ?? (ch === 'NOTIFY' ? 'Counselor' : 'Client');
  return { kind: 'SEND', channel: ch, recipients: { [r]: { on: true } } } as DefNode;
}

type Sel =
  | { kind: 'recip'; path: Seg[]; r: RecipientKey }
  | { kind: 'wait'; path: Seg[] }
  | { kind: 'cond'; path: Seg[] }
  | null;

// ── component ──────────────────────────────────────────────────────
export function WorkflowCanvasBuilder({
  initial, waTemplates, emailTemplates, onClose, onSaved,
}: {
  initial: Workflow | null;
  waTemplates: TemplateOption[];
  emailTemplates: TemplateOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [triggerType, setTriggerType] = useState<TriggerType>(initial?.triggerType ?? 'LEAD_CREATED');
  const [offsetMinutes, setOffsetMinutes] = useState<number>(
    Number((initial?.triggerConfig as any)?.offsetMinutes) || 0,
  );
  const [statuses, setStatuses] = useState(
    Array.isArray((initial?.triggerConfig as any)?.statuses) ? ((initial!.triggerConfig as any).statuses as string[]).join(', ') : '',
  );
  const [stopOnReply, setStopOnReply] = useState(initial?.stopOnReply ?? true);
  const [stopOnConvert, setStopOnConvert] = useState(initial?.stopOnConvertOrLost ?? true);
  const [quietStart, setQuietStart] = useState(initial?.quietHoursStart ?? '');
  const [quietEnd, setQuietEnd] = useState(initial?.quietHoursEnd ?? '');
  const [maxPerDay, setMaxPerDay] = useState(initial?.maxPerLeadPerDay != null ? String(initial.maxPerLeadPerDay) : '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? false);
  const [def, setDef] = useState<WorkflowDefinition>(
    initial?.definition?.steps?.length ? initial.definition : { steps: [newSend('WHATSAPP')] },
  );
  const [sel, setSel] = useState<Sel>(null);
  const [menu, setMenu] = useState<{ container: Seg[]; index: number; ch?: ChannelKey } | null>(null);
  const [saving, setSaving] = useState(false);
  const [voiceAgents, setVoiceAgents] = useState<{ id: string; name: string; isActive: boolean }[]>([]);
  const [smsTemplates, setSmsTemplates] = useState<{ id: string; name: string; body: string; isActive: boolean }[]>([]);

  useEffect(() => {
    // For the AI Voice Call node's agent picker.
    api.get('/voice/agents').then((r) => setVoiceAgents(r.data || [])).catch(() => setVoiceAgents([]));
    // For the Send SMS node's template picker.
    api.get('/sms-templates')
      .then((r) => setSmsTemplates((Array.isArray(r.data) ? r.data : []).filter((t: any) => t.isActive)))
      .catch(() => setSmsTemplates([]));
  }, []);

  const mutate = (fn: (d: WorkflowDefinition) => void) => setDef((prev) => { const d = clone(prev); fn(d); return d; });

  // ── add menu ──
  const openMenu = (container: Seg[], index: number) => { setMenu({ container, index }); };
  const insertNode = (n: DefNode) => {
    if (!menu) return;
    mutate((d) => { arrAt(d, [...menu.container, { i: 0 }]).splice(menu.index, 0, n); });
    setMenu(null);
  };
  const pickMenuChannel = (ch: ChannelKey | 'WAIT' | 'IF') => {
    if (ch === 'WAIT') { insertNode({ kind: 'WAIT', delayMinutes: 60 }); return; }
    if (ch === 'IF') { insertNode({ kind: 'IF', cond: { field: 'replied' }, yes: [], no: [] }); return; }
    // lane (inside IF) → single recipient via 2nd step; top-level → multi via chips
    if (menu && menu.container.length > 0) { setMenu({ ...menu, ch }); return; }
    insertNode(newSend(ch));
  };
  const pickMenuRecipient = (r: RecipientKey) => { if (menu?.ch) insertNode(newSend(menu.ch, r)); };

  const removeAt = (path: Seg[]) => { mutate((d) => { arrAt(d, path).splice(path[path.length - 1].i, 1); }); setSel(null); };
  const toggleRecip = (path: Seg[], r: RecipientKey) => {
    mutate((d) => { const n = nodeAt(d, path) as any; if (!n.recipients[r]) n.recipients[r] = { on: true }; else n.recipients[r].on = true; });
    setSel({ kind: 'recip', path, r });
  };

  const save = async () => {
    if (!name.trim()) { toast('error', 'Give the workflow a name.'); return; }
    setSaving(true);
    try {
      // Keep any other keys already on the config (e.g. the ready-made tag).
      const { statuses: _s, offsetMinutes: _o, ...keep } = ((initial?.triggerConfig ?? {}) as Record<string, unknown>);
      const triggerConfig: Record<string, unknown> = { ...keep };
      if (triggerType === 'LEAD_STATUS_CHANGED' && statuses.trim()) {
        triggerConfig.statuses = statuses.split(',').map((s) => s.trim()).filter(Boolean);
      }
      if (triggerType === 'FOLLOWUP_DUE' || triggerType === 'MEETING_UPCOMING') triggerConfig.offsetMinutes = offsetMinutes;
      const body = {
        name: name.trim(), triggerType, triggerConfig, isActive,
        stopOnReply, stopOnConvertOrLost: stopOnConvert,
        quietHoursStart: quietStart.trim() || null, quietHoursEnd: quietEnd.trim() || null,
        maxPerLeadPerDay: maxPerDay.trim() ? Math.max(1, Number(maxPerDay) || 0) || null : null,
        definition: def,
      };
      if (initial) await updateWorkflow(initial.id, body); else await createWorkflow(body);
      toast('success', initial ? 'Workflow updated' : 'Workflow created');
      onSaved();
    } catch (e: any) {
      toast('error', e?.response?.data?.message ?? 'Save failed');
    } finally { setSaving(false); }
  };

  // ── render helpers ──
  const Plus = ({ container, index }: { container: Seg[]; index: number }) => {
    const open = menu && JSON.stringify(menu.container) === JSON.stringify(container) && menu.index === index;
    if (!open) return <button className="wf-plus" onClick={() => openMenu(container, index)}>+</button>;
    const lane = container.length > 0;
    return (
      <div className="wf-menu">
        {!menu!.ch ? (
          <>
            <div className="wf-mhd">Add a step</div>
            {(['WHATSAPP', 'EMAIL', 'SMS', 'NOTIFY', 'CALL'] as ChannelKey[]).map((c) => (
              <button key={c} onClick={() => pickMenuChannel(c)}><span className="wf-mic" style={{ background: CH_META[c].bg, color: CH_META[c].fg }}>{CH_META[c].icon}</span>{CH_META[c].title}</button>
            ))}
            <button onClick={() => pickMenuChannel('WAIT')}><span className="wf-mic" style={{ background: '#f1f5f9' }}>⏱</span>Wait / delay</button>
            {!lane && <button onClick={() => pickMenuChannel('IF')}><span className="wf-mic" style={{ background: '#f3e8ff', color: '#9333ea' }}>🔀</span>If / Else</button>}
            <button className="wf-back" onClick={() => setMenu(null)}>✕ close</button>
          </>
        ) : (
          <>
            <div className="wf-mhd">{CH_META[menu!.ch].title} — to whom?</div>
            {recipientsFor(menu!.ch).map((r) => (
              <button key={r} onClick={() => pickMenuRecipient(r)}><span className="wf-mic" style={{ background: '#f1f5f9' }}>{RICON[r]}</span>{rLabel(r)}</button>
            ))}
            <button className="wf-back" onClick={() => setMenu({ ...menu!, ch: undefined })}>← back</button>
          </>
        )}
      </div>
    );
  };

  const renderNode = (node: DefNode, path: Seg[]) => {
    if (node.kind === 'WAIT') {
      return (
        <div className="wf-node" onClick={() => setSel({ kind: 'wait', path })}>
          <div className="wf-row"><span className="wf-ic" style={{ background: '#f1f5f9', color: '#475569' }}>⏱</span><div className="wf-ttl">Wait</div>
            <button className="wf-del" onClick={(e) => { e.stopPropagation(); removeAt(path); }}><Trash2 className="h-3.5 w-3.5" /></button></div>
          <div className="wf-sub" style={{ marginLeft: 39 }}>Wait {formatDelay(node.delayMinutes)}</div>
        </div>
      );
    }
    if (node.kind === 'IF') {
      const ifSel = sel?.kind === 'cond' && JSON.stringify(sel.path) === JSON.stringify(path);
      return (
        <>
          <div className={`wf-ifnode ${ifSel ? 'sel' : ''}`} onClick={() => setSel({ kind: 'cond', path })}>
            <div className="wf-row"><span className="wf-cic">🔀</span><div><div className="wf-ttl">If / Else</div><div className="wf-sub">{COND_FIELDS[node.cond.field] || 'condition'}{node.cond.value ? ` = ${node.cond.value}` : ''}</div></div>
              <button className="wf-del" onClick={(e) => { e.stopPropagation(); removeAt(path); }}><Trash2 className="h-3.5 w-3.5" /></button></div>
          </div>
          <div className="wf-lanes">
            {(['yes', 'no'] as const).map((b) => (
              <div className="wf-lane" key={b}>
                <div className={`wf-lanehd ${b}`}>{b === 'yes' ? '✓ Yes' : '✕ No'}</div>
                {(node as any)[b].map((cn: DefNode, ci: number) => (
                  <div key={ci} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div className="wf-arrow" />{renderNode(cn, [...path.slice(0, -1), { ...path[path.length - 1], branch: b }, { i: ci }])}
                  </div>
                ))}
                <div className="wf-arrow" />
                <Plus container={[...path.slice(0, -1), { ...path[path.length - 1], branch: b }]} index={(node as any)[b].length} />
              </div>
            ))}
          </div>
        </>
      );
    }
    // SEND
    const m = CH_META[node.channel];
    const recips = recipientsFor(node.channel);
    return (
      <div className="wf-group">
        <div className="wf-node"><div className="wf-row"><span className="wf-ic" style={{ background: m.bg, color: m.fg }}>{m.icon}</span><div className="wf-ttl">{m.title}</div>
          <button className="wf-del" onClick={(e) => { e.stopPropagation(); removeAt(path); }}><Trash2 className="h-3.5 w-3.5" /></button></div></div>
        <div className="wf-kids">
          {recips.map((r) => {
            const on = (node.recipients as any)[r]?.on;
            const rs = sel?.kind === 'recip' && sel.r === r && JSON.stringify(sel.path) === JSON.stringify(path);
            return (
              <div className="wf-kid" key={r}>
                <div className={`wf-rbox ${on ? 'on' : ''} ${rs ? 'sel' : ''}`} onClick={() => toggleRecip(path, r)}>{on ? '✓ ' : ''}{RICON[r]} {rLabel(r)}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-100">
      <Style />
      {/* Top bar */}
      <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">⚡</span>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Workflow name" className="w-60 rounded-md border border-gray-200 px-3 py-2 text-sm font-semibold" />
        <SelectField value={triggerType} onChange={(e) => setTriggerType(e.target.value as TriggerType)} className="rounded-full bg-slate-100 px-3 py-2 text-xs">
          {(Object.keys(TRIGGER_LABELS) as TriggerType[]).map((t) => <option key={t} value={t}>Trigger: {TRIGGER_LABELS[t]}</option>)}
        </SelectField>
        <div className="flex-1" />
        <label className="flex items-center gap-2 text-sm text-slate-700">Active
          <button onClick={() => setIsActive((v) => !v)} className={`relative h-6 w-11 rounded-full ${isActive ? 'bg-indigo-500' : 'bg-slate-300'}`}><span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${isActive ? 'translate-x-5' : ''}`} /></button>
        </label>
        <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm text-slate-600">Cancel</button>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:bg-slate-300">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save</button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Canvas */}
        <div className="wf-canvas flex-1 overflow-auto">
          <div className="wf-node trigger"><div className="wf-row"><span className="wf-ic" style={{ background: '#c7d2fe', color: '#4338ca' }}>⚡</span><div><div className="wf-ttl">Trigger</div><div className="wf-sub">{TRIGGER_LABELS[triggerType]}</div></div></div></div>
          {triggerType === 'LEAD_STATUS_CHANGED' && (
            <input value={statuses} onChange={(e) => setStatuses(e.target.value)} placeholder="Statuses (comma sep), blank = any" className="mt-2 w-72 rounded-md border border-gray-200 px-3 py-1.5 text-xs" />
          )}
          {(triggerType === 'FOLLOWUP_DUE' || triggerType === 'MEETING_UPCOMING') && (
            <div className="mt-2 w-72">
              <SelectField value={String(offsetMinutes)} onChange={(e) => setOffsetMinutes(Number(e.target.value))} className="h-8 w-full rounded-md border border-gray-200 px-3 text-xs">
                {FOLLOWUP_OFFSETS.map((o) => <option key={o.value} value={String(o.value)}>Start: {triggerType === 'MEETING_UPCOMING' && o.value === 0 ? 'At the meeting time' : o.label}</option>)}
              </SelectField>
            </div>
          )}
          <div className="wf-arrow" /><Plus container={[]} index={0} />
          {def.steps.map((n, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div className="wf-arrow" />{renderNode(n, [{ i }])}
              <div className="wf-arrow" /><Plus container={[]} index={i + 1} />
            </div>
          ))}
          <div className="wf-endhint">Use "+" to add a step or If/Else · click a box to edit its settings</div>
        </div>

        {/* Config */}
        <div className="w-80 overflow-y-auto border-l border-gray-200 bg-white p-4">
          <ConfigPanel
            def={def} sel={sel} mutate={mutate} removeAt={removeAt} triggerType={triggerType}
            waTemplates={waTemplates} emailTemplates={emailTemplates} voiceAgents={voiceAgents} smsTemplates={smsTemplates}
            stopOnReply={stopOnReply} setStopOnReply={setStopOnReply}
            stopOnConvert={stopOnConvert} setStopOnConvert={setStopOnConvert}
            quietStart={quietStart} setQuietStart={setQuietStart} quietEnd={quietEnd} setQuietEnd={setQuietEnd}
            maxPerDay={maxPerDay} setMaxPerDay={setMaxPerDay}
          />
        </div>
      </div>
    </div>
  );
}

// ── Config panel (right) ───────────────────────────────────────────
function ConfigPanel(p: any) {
  const { def, sel, mutate, removeAt, waTemplates, emailTemplates, voiceAgents, smsTemplates } = p;
  const varFields = [...(EVENT_VAR_FIELDS[p.triggerType as TriggerType] ?? []), ...VAR_FIELDS];
  if (!sel) {
    return (
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Workflow settings</p>
        <label className="mb-1 flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={p.stopOnReply} onChange={(e) => p.setStopOnReply(e.target.checked)} />Stop if the client replies</label>
        <label className="mb-3 flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={p.stopOnConvert} onChange={(e) => p.setStopOnConvert(e.target.checked)} />Stop if converted / lost</label>
        <p className="mb-1 text-xs font-medium text-slate-600">Quiet hours <span className="font-normal text-slate-400">(client only)</span></p>
        <div className="mb-3 flex items-center gap-2 text-xs"><TimeDropdown className="w-28" placeholder="Start" value={p.quietStart} onChange={p.setQuietStart} /> to <TimeDropdown className="w-28" placeholder="End" value={p.quietEnd} onChange={p.setQuietEnd} /></div>
        <p className="mb-1 text-xs font-medium text-slate-600">Max msgs per lead / day</p>
        <input type="number" min={1} value={p.maxPerDay} onChange={(e) => p.setMaxPerDay(e.target.value)} placeholder="∞" className="w-24 rounded-md border border-gray-200 px-2 py-1 text-xs" />
        <p className="mt-6 text-center text-xs text-slate-400">Click any box to edit its settings.</p>
      </div>
    );
  }
  const node = nodeAt(def, sel.path) as any;
  if (!node) return <div className="text-xs text-slate-400">—</div>;

  if (sel.kind === 'cond') {
    return (
      <div>
        <h3 className="mb-1 flex items-center gap-2 text-sm font-bold"><span className="flex h-6 w-6 items-center justify-center rounded-md bg-purple-100 text-purple-600">🔀</span>If / Else</h3>
        <p className="mb-3 text-xs text-slate-400">Condition — Yes/No me split</p>
        <label className="mb-1 block text-xs font-normal text-slate-600">If…</label>
        <SelectField value={node.cond.field} onChange={(e) => mutate((d: any) => { (nodeAt(d, sel.path) as any).cond.field = e.target.value; })} className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm">
          {Object.keys(COND_FIELDS).map((k) => <option key={k} value={k}>{COND_FIELDS[k]}</option>)}
        </SelectField>
        {(node.cond.field === 'status' || node.cond.field === 'source') && (
          <>
            <label className="mb-1 mt-2 block text-xs font-normal text-slate-600">{node.cond.field === 'status' ? 'Status is' : 'Source is'}</label>
            <input value={node.cond.value ?? ''} onChange={(e) => mutate((d: any) => { (nodeAt(d, sel.path) as any).cond.value = e.target.value; })} placeholder={node.cond.field === 'status' ? 'e.g. Converted' : 'e.g. Facebook'} className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm" />
          </>
        )}
        <p className="mt-2 text-[11px] text-slate-400">✓ Yes = condition sahi · ✕ No = galat</p>
        <button className="mt-4 w-full rounded-md border border-red-200 bg-red-50 py-2 text-sm font-medium text-red-600" onClick={() => removeAt(sel.path)}>Delete this If/Else</button>
      </div>
    );
  }
  if (sel.kind === 'wait') {
    return (
      <div>
        <h3 className="mb-1 flex items-center gap-2 text-sm font-bold"><span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-slate-600">⏱</span>Wait</h3>
        <label className="mb-1 mt-3 block text-xs font-normal text-slate-600">Wait for</label>
        <div className="flex gap-2">
          <input type="number" min={0} value={splitDelay(node.delayMinutes).value}
            onChange={(e) => mutate((d: any) => {
              const cur = splitDelay((nodeAt(d, sel.path) as any).delayMinutes);
              const v = Math.max(0, Number(e.target.value) || 0);
              (nodeAt(d, sel.path) as any).delayMinutes = v * unitMins(cur.unit);
            })}
            className="w-24 rounded-md border border-gray-200 px-2 py-1.5 text-sm" />
          <SelectField value={splitDelay(node.delayMinutes).unit}
            onChange={(e) => mutate((d: any) => {
              const cur = splitDelay((nodeAt(d, sel.path) as any).delayMinutes);
              (nodeAt(d, sel.path) as any).delayMinutes = cur.value * unitMins(e.target.value as DelayUnit);
            })}
            className="rounded-md border border-gray-200 px-2 py-1.5 text-sm">
            {DELAY_UNITS.map((u) => <option key={u.unit} value={u.unit}>{u.label}</option>)}
          </SelectField>
        </div>
        <p className="mt-1 text-[11px] text-slate-400">= {node.delayMinutes} min total</p>
        <button className="mt-4 w-full rounded-md border border-red-200 bg-red-50 py-2 text-sm font-medium text-red-600" onClick={() => removeAt(sel.path)}>Delete this step</button>
      </div>
    );
  }
  // recipient
  const r: RecipientKey = sel.r;
  const cfg = node.recipients[r] ?? { on: true };
  const setCfg = (patch: any) => mutate((d: any) => { const nn = nodeAt(d, sel.path) as any; nn.recipients[r] = { ...(nn.recipients[r] ?? { on: true }), ...patch }; });
  const m = CH_META[node.channel as ChannelKey];
  const isCouns = r === 'Counselor';
  const waOpts = waTemplates as TemplateOption[];
  const selectedTpl = waOpts.find((t) => t.id === cfg.templateId);
  return (
    <div>
      <h3 className="mb-1 flex items-center gap-2 text-sm font-bold"><span className="flex h-6 w-6 items-center justify-center rounded-md" style={{ background: m.bg, color: m.fg }}>{m.icon}</span>{m.title}</h3>
      <p className="mb-3 text-xs text-slate-400">Recipient: <b className="text-indigo-700">{RICON[r]} {rLabel(r)}</b></p>
      {node.channel === 'WHATSAPP' && (
        <>
          <label className="mb-1 block text-xs font-normal text-slate-600">WhatsApp template (approved)</label>
          <SelectField value={cfg.templateId ?? ''} onChange={(e) => {
            const id = e.target.value || null;
            // Persist the same variable defaults the dropdowns display, so a
            // template selected without touching each selector still saves a
            // real mapping. Otherwise the engine sees an empty mapping and
            // fills every {{n}} with the lead name.
            const tpl = waOpts.find((t) => t.id === id);
            const seeded: Record<string, string> = {};
            for (let k = 0; k < (tpl?.vars ?? 0); k++) {
              seeded[String(k + 1)] = isCouns ? (k === 0 ? 'counselor.name' : 'lead.name') : 'lead.name';
            }
            setCfg({ templateId: id, variableMapping: seeded });
          }} className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm">
            <option value="">— Select —</option>
            {waOpts.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </SelectField>
          {selectedTpl?.body && (
            <div className="mt-2 rounded-lg bg-[#e9fbe5] p-2.5">
              <p className="mb-1 text-[10px] font-medium text-slate-500">Template body</p>
              <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-slate-700">{selectedTpl.body}</p>
            </div>
          )}
          {selectedTpl && (selectedTpl.vars ?? 0) > 0 && (
            <div className="mt-2 space-y-1.5">
              <p className="text-[10px] font-medium text-slate-500">Fill variables</p>
              {Array.from({ length: selectedTpl.vars ?? 0 }).map((_, k) => {
                const key = String(k + 1);
                const vm = cfg.variableMapping ?? {};
                return (
                  <div key={key} className="flex items-center gap-2">
                    <span className="w-8 font-mono text-[10px] text-slate-400">{`{{${key}}}`}</span>
                    <SelectField value={vm[key] ?? (isCouns ? (k === 0 ? 'counselor.name' : 'lead.name') : 'lead.name')} onChange={(e) => setCfg({ variableMapping: { ...vm, [key]: e.target.value } })} className="flex-1 rounded-md border border-gray-200 px-2 py-1 text-xs">
                      {varFields.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </SelectField>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      {node.channel === 'EMAIL' && (
        <>
          <label className="mb-1 block text-xs font-normal text-slate-600">Email template</label>
          <SelectField value={cfg.templateId ?? ''} onChange={(e) => setCfg({ templateId: e.target.value || null })} className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm">
            <option value="">— Select —</option>
            {(emailTemplates as TemplateOption[]).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </SelectField>
        </>
      )}
      {node.channel === 'SMS' && (
        <>
          {(smsTemplates ?? []).length > 0 && (
            <>
              <label className="mb-1 block text-xs font-normal text-slate-600">SMS template</label>
              <SelectField
                defaultValue=""
                onChange={(e) => {
                  const tpl = (smsTemplates as { id: string; body: string }[]).find((t) => t.id === e.target.value);
                  if (tpl) setCfg({ message: tpl.body });
                }}
                className="mb-2 w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm"
              >
                <option value="">— Start from a template —</option>
                {(smsTemplates as { id: string; name: string }[]).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </SelectField>
            </>
          )}
          <label className="mb-1 block text-xs font-normal text-slate-600">SMS message</label>
          <textarea value={cfg.message ?? ''} onChange={(e) => setCfg({ message: e.target.value })} rows={3} placeholder="Hi {{lead.name}}, ..." className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm" />
        </>
      )}
      {node.channel === 'NOTIFY' && (
        <>
          <label className="mb-1 block text-xs font-normal text-slate-600">Notification text</label>
          <textarea value={cfg.message ?? ''} onChange={(e) => setCfg({ message: e.target.value })} rows={3} placeholder="New lead {{lead.name}} assigned to you." className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm" />
        </>
      )}
      {node.channel === 'CALL' && (
        <>
          <label className="mb-1 block text-xs font-normal text-slate-600">AI voice agent</label>
          <SelectField value={cfg.agentId ?? ''} onChange={(e) => setCfg({ agentId: e.target.value || null })} className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm">
            <option value="">— Select agent —</option>
            {((voiceAgents ?? []) as { id: string; name: string; isActive: boolean }[]).map((a) => (
              <option key={a.id} value={a.id}>{a.name}{a.isActive ? '' : ' (inactive)'}</option>
            ))}
          </SelectField>
          {(voiceAgents ?? []).length === 0 && (
            <p className="mt-2 text-[11px] text-amber-600">No agents — create one first in AI Assistant → Voice Agents.</p>
          )}
          <p className="mt-2 text-[11px] text-slate-400">The lead will get an AI call. Skipped if the master switch is off. Consented leads only.</p>
        </>
      )}
      <button className="mt-4 w-full rounded-md border border-red-200 bg-red-50 py-2 text-sm font-medium text-red-600" onClick={() => setCfg({ on: false })}>Remove {rLabel(r)} from this step</button>
    </div>
  );
}

// ── scoped styles (canvas tree visuals) ────────────────────────────
function Style() {
  return (
    <style>{`
      .wf-canvas { background: radial-gradient(#e2e8f0 1px, transparent 1px); background-size: 18px 18px; padding: 28px 0 60px; display: flex; flex-direction: column; align-items: center; }
      .wf-node { width: 300px; background: #fff; border: 2px solid #e2e8f0; border-radius: 14px; padding: 12px 14px; box-shadow: 0 1px 3px rgba(0,0,0,.05); cursor: pointer; }
      .wf-node.trigger { background: #eef2ff; border-color: #c7d2fe; cursor: default; }
      .wf-row { display: flex; align-items: center; gap: 9px; }
      .wf-ic { width: 30px; height: 30px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0; }
      .wf-ttl { font-size: 13px; font-weight: 600; }
      .wf-sub { font-size: 11px; color: #64748b; }
      .wf-del { margin-left: auto; color: #cbd5e1; background: none; border: none; cursor: pointer; }
      .wf-del:hover { color: #ef4444; }
      .wf-group { display: flex; flex-direction: column; align-items: center; }
      .wf-kids { position: relative; display: flex; gap: 14px; padding-top: 28px; }
      .wf-kids::before { content:''; position:absolute; top:0; left:50%; transform:translateX(-50%); width:2px; height:14px; background:#cbd5e1; }
      .wf-kids::after { content:''; position:absolute; top:14px; left:16%; right:16%; height:2px; background:#cbd5e1; }
      .wf-kid { position: relative; }
      .wf-kid::before { content:''; position:absolute; top:-14px; left:50%; transform:translateX(-50%); width:2px; height:14px; background:#cbd5e1; }
      .wf-rbox { border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 8px 13px; font-size: 12px; font-weight: 600; color: #94a3b8; background: #fff; cursor: pointer; white-space: nowrap; }
      .wf-rbox.on { border-color: #6366f1; background: #eef2ff; color: #4338ca; }
      .wf-rbox.sel { box-shadow: 0 0 0 3px #e0e7ff; }
      .wf-ifnode { width: 290px; background: #fff; border: 2px solid #e9d5ff; border-radius: 14px; padding: 12px 14px; cursor: pointer; }
      .wf-ifnode.sel { border-color: #a855f7; box-shadow: 0 0 0 4px #f3e8ff; }
      .wf-cic { width: 30px; height: 30px; border-radius: 8px; background: #f3e8ff; color: #9333ea; display: flex; align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0; }
      .wf-lanes { position: relative; display: flex; gap: 30px; padding-top: 28px; align-items: flex-start; }
      .wf-lanes::before { content:''; position:absolute; top:0; left:50%; transform:translateX(-50%); width:2px; height:14px; background:#cbd5e1; }
      .wf-lanes::after { content:''; position:absolute; top:14px; left:25%; right:25%; height:2px; background:#cbd5e1; }
      .wf-lane { position: relative; display: flex; flex-direction: column; align-items: center; gap: 4px; }
      .wf-lane::before { content:''; position:absolute; top:-14px; left:50%; transform:translateX(-50%); width:2px; height:14px; background:#cbd5e1; }
      .wf-lanehd { font-size: 11px; font-weight: 700; padding: 3px 14px; border-radius: 20px; }
      .wf-lanehd.yes { background: #dcfce7; color: #16a34a; }
      .wf-lanehd.no { background: #fee2e2; color: #dc2626; }
      .wf-arrow { width: 2px; height: 16px; background: #cbd5e1; }
      .wf-plus { width: 26px; height: 26px; border-radius: 50%; border: 1.5px dashed #c7d2fe; color: #6366f1; background: #fff; font-size: 16px; cursor: pointer; }
      .wf-plus:hover { background: #eef2ff; }
      .wf-menu { width: 220px; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 8px 24px rgba(2,6,23,.12); padding: 6px; }
      .wf-mhd { font-size: 11px; color: #94a3b8; padding: 6px 8px 4px; font-weight: 600; }
      .wf-menu button { display: flex; align-items: center; gap: 9px; width: 100%; text-align: left; border: none; background: none; padding: 8px 9px; font-size: 13px; font-weight: 500; color: #334155; border-radius: 8px; cursor: pointer; }
      .wf-menu button:hover { background: #f5f3ff; }
      .wf-mic { width: 24px; height: 24px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0; }
      .wf-back { color: #64748b; font-size: 12px; }
      .wf-endhint { font-size: 11px; color: #94a3b8; margin-top: 6px; }
    `}</style>
  );
}
